'use client';

import type {
  PeopleCultureComponent,
  TransformedPeopleCultureData,
} from '@/types/people-and-culture';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import '../people-and-culture-animations.css';
import { usePeopleAndCultureAnimations } from '../lib/usePeopleAndCultureAnimations';
import PeopleHero from '../components/PeopleHero';
import CommitmentsSection from '../components/CommitmentsSection';
import InitiativesSection from '../components/InitiativesSection';
import LifeAtWonderSection from '../components/LifeAtWonderSection';
import PhotoMarquee from '../components/PhotoMarquee';
import TestimonialsSection from '../components/TestimonialsSection';
import JoinCtaSection from '../components/JoinCtaSection';

interface PeopleAndCultureClientProps {
  locale: string;
  data: TransformedPeopleCultureData;
}

type SectionRefs = ReturnType<typeof usePeopleAndCultureAnimations>;

function renderPeopleCultureComponent(
  component: PeopleCultureComponent,
  locale: string,
  refs: SectionRefs,
) {
  switch (component.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={component.id} zone={component} />;

    case 'people-culture.card-section':
      return <CommitmentsSection key={component.id} sectionRef={refs.commitRef} zone={component} />;

    case 'people-culture.image-grid-slider':
      return (
        <InitiativesSection
          key={component.id}
          locale={locale}
          sectionRef={refs.initRef}
          zone={component}
        />
      );

    case 'people-culture.bento-grid':
      return <LifeAtWonderSection key={component.id} sectionRef={refs.lifeRef} zone={component} />;

    case 'people-culture.image-ticker':
      return <PhotoMarquee key={component.id} zone={component} />;

    case 'people-culture.wonder-testimonial':
      return <TestimonialsSection key={component.id} sectionRef={refs.testRef} zone={component} />;

    case 'people-culture.pnc-pre-footer':
      return <JoinCtaSection key={component.id} locale={locale} zone={component} />;

    default:
      return null;
  }
}

export default function PeopleAndCultureClient({ locale, data }: PeopleAndCultureClientProps) {
  const refs = usePeopleAndCultureAnimations();

  const hasBannerContent =
    Boolean(data.bannerMedia) ||
    data.bannerHeading.length > 0 ||
    Boolean(data.bannerDescription?.trim()) ||
    data.bannerStats.length > 0;

  return (
    <div ref={refs.pageRef} className="min-h-screen bg-white font-figtree text-[#0a0a0a]">
      {hasBannerContent ? (
        <PeopleHero
          sectionRef={refs.heroRef}
          bannerMedia={data.bannerMedia}
          bannerHeading={data.bannerHeading}
          bannerDescription={data.bannerDescription}
          bannerStats={data.bannerStats}
        />
      ) : null}

      {data.components.map((component) => renderPeopleCultureComponent(component, locale, refs))}
    </div>
  );
}
